import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { trpcVanilla } from "@client/trpc";
import { useEffect } from "react";
import { toast } from "sonner";
import { saveAuthToken } from "./auth";
import { parseMobileAuthCallback } from "./mobileOAuth";

// Marks that the cold-start launch URL has already been successfully consumed.
// Lives in sessionStorage so it survives the success `window.location.reload()`
// (and a later sign-out reload) but resets on a true cold start, where the
// WebView — and its sessionStorage — is recreated.
const LAUNCH_URL_CONSUMED_KEY = "mobileAuthLaunchUrlConsumed";

// In-memory guard so `appUrlOpen` and `getLaunchUrl()` can't both handle the
// same launch URL in parallel (duplicate exchanges / double reload). Resets on
// reload (the module re-evaluates), which is the only path that needs a retry.
let handlingDeepLink = false;

/**
 * Listens for the app being opened via its custom URL scheme and, if the
 * incoming URL carries a short-lived authorization code (the magic-link deep
 * link from the email confirmation page), exchanges it for the session token
 * and reloads so the session is picked up.
 *
 * The interactive Google flow does NOT rely on this — ASWebAuthenticationSession
 * returns its callback URL directly to the plugin. This handler covers the
 * magic-link path, where the link is tapped outside the app (in Mail/Safari)
 * and relaunches it.
 */
export function useMobileAuthDeepLink(): void {
  useEffect(() => {
    // Runs on any native shell (iPad + iPhone). The magic-link deep link
    // returns into the app via the custom URL scheme regardless of device.
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    async function applyDeepLink(url: string | undefined): Promise<void> {
      if (!url || handlingDeepLink) {
        return;
      }
      const { code } = parseMobileAuthCallback(url);
      if (!code) {
        return;
      }
      handlingDeepLink = true;
      try {
        // Magic-link codes are unbound (no PKCE verifier) — short-lived and
        // single-use, gated by the emailed link itself.
        const { sessionToken } =
          await trpcVanilla.exchangeMobileAuthCode.mutate({ code });
        const bearerChanged = saveAuthToken(sessionToken);
        // Mark the launch URL consumed only after a successful exchange — so a
        // transient failure doesn't permanently burn a still-valid link — then
        // navigate to pick up the session. After that `getLaunchUrl()`
        // returns this same URL, but the flag makes us skip it (no loop), and
        // it keeps sign-out sticky (the persisted flag blocks a replay).
        window.sessionStorage.setItem(LAUNCH_URL_CONSUMED_KEY, "1");
        if (bearerChanged) {
          // Identity transition: a reload would inherit the current URL, which
          // on a cold launch the boot redirect may already have rewritten to
          // the PREVIOUS user's saved round. Land the new session on "/".
          window.location.replace("/");
        } else {
          window.location.reload();
        }
      } catch (error) {
        // Expired/replayed codes are expected (e.g. re-opening an old link).
        // Release the guard so a fresh open (user re-taps) can retry, and tell
        // the user instead of failing silently.
        handlingDeepLink = false;
        console.error("Failed to exchange mobile auth code", error);
        toast.error(
          "That sign-in link didn't work — it may have expired. Please request a new one.",
        );
      }
    }

    // `appUrlOpen` fires once per real deep-link open (no replay).
    const listenerHandle = App.addListener("appUrlOpen", ({ url }) => {
      void applyDeepLink(url);
    });

    // Cold start: the app may have been launched by the deep link itself, where
    // `appUrlOpen` does not fire. `getLaunchUrl()` keeps returning that same URL
    // for the life of the app process — across reloads and even after sign-out —
    // so skip it once it's been consumed.
    void App.getLaunchUrl().then((result) => {
      if (window.sessionStorage.getItem(LAUNCH_URL_CONSUMED_KEY)) {
        return;
      }
      void applyDeepLink(result?.url);
    });

    return () => {
      void listenerHandle.then((handle) => handle.remove());
    };
  }, []);
}
